<template>
  <div class="nutrition-table">
    <div class="nutrition-header">
      <span class="label">计量单位：</span>
      <el-select v-model="measureUnit" style="width: 150px" @change="onMeasureUnitChange">
        <el-option label="每100g" value="每100g" />
        <el-option label="每100克" value="每100克" />
        <el-option label="每100ml" value="每100ml" />
        <el-option label="每100毫升" value="每100毫升" />
      </el-select>
    </div>
    <table class="table">
      <thead>
        <tr>
          <th class="col-project">项目</th>
          <th class="col-value">每份含量</th>
          <th class="col-nrv">营养成分参考值</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="module in displayModules" :key="module.moduleId">
          <td class="col-project">
            <span v-if="module.valueRequired && !module._value" class="required-mark"> * </span>{{ module.prefix }}
          </td>
          <td class="col-value">
            <el-input-digit
              v-model.number="module._value"
              :precision="module.validateRule.precision"
              :max="module.validateRule.max"
              :min="module.validateRule.min"
              style="width: 120px"
              @change="onValueChange(module)"
            >
              <span>{{ getUnitName(module) }}</span>
            </el-input-digit>
            <span v-if="module.valueRequired && !module._value" class="el-form-item__error error-text"> 必填</span>
          </td>
          <td class="col-nrv">
            {{ getNrvDisplay(module._nrvPercent) }}
            <i
              v-if="!module.valueRequired"
              class="el-icon-delete delete-icon"
              @click="deleteItem(module.moduleId)"
              style="margin-left: 8px"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>
    <el-button type="text" @click="showAddDialog = true" v-if="availableModules.length > 0"> + 添加营养成分 </el-button>

    <!-- 添加营养成分弹窗 -->
    <el-dialog title="选择营养成分" :visible.sync="showAddDialog" width="500px" :append-to-body="false">
      <div class="add-dialog-content">
        <el-checkbox-group v-model="selectedModules">
          <div v-for="mod in availableModules" :key="mod.moduleId" class="checkbox-item">
            <el-checkbox :label="mod.moduleId">{{ mod.prefix }}</el-checkbox>
          </div>
        </el-checkbox-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showAddDialog = false">取 消</el-button>
        <el-button type="primary" @click="confirmAdd">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';

export default {
  name: 'NutritionTable',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    props: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      measureUnit: '每100g',
      modules: [],
      showAddDialog: false,
      selectedModules: [],
      selectedOptionalModuleIds: [],
    };
  },
  computed: {
    measureTemplates() {
      return (this.props && this.props.measureTemplates) || [];
    },
    templateId() {
      const mt = this.measureTemplates[0];
      return mt ? mt.templateId : 0;
    },
    valueModules() {
      const mt = this.measureTemplates[0];
      return mt ? mt.valueModules || [] : [];
    },
    requiredModules() {
      return this.valueModules.filter(m => m.valueRequired);
    },
    optionalModules() {
      return this.valueModules.filter(m => !m.valueRequired);
    },
    displayModules() {
      // 从 value 中提取已填写的 module_id 作为已选项
      const templateData = this.value.find(v => v.measure_info && v.measure_info.template_id === this.templateId);
      const filledModuleIds = (templateData && templateData.measure_info && templateData.measure_info.values)
        ? templateData.measure_info.values.map(v => v.module_id)
        : [];

      // 合并已选项：从 value 中的 + 用户手动选的
      const allOptionalIds = [...new Set([...filledModuleIds, ...this.selectedOptionalModuleIds])];

      const result = [];

      // 1. 必填模块始终展示（从 this.modules 中取，_value 才有值）
      this.requiredModules.forEach(rm => {
        const mod = this.modules.find(m => m.moduleId === rm.moduleId);
        if (mod) result.push(mod);
      });

      // 2. 可选模块：按 value 中的顺序排列
      this.optionalModules.filter(m => filledModuleIds.includes(m.moduleId)).forEach(om => {
        const mod = this.modules.find(m => m.moduleId === om.moduleId);
        if (mod) result.push(mod);
      });

      // 3. 新添加的可选模块追加在最后
      this.optionalModules.filter(m => allOptionalIds.includes(m.moduleId) && !filledModuleIds.includes(m.moduleId)).forEach(om => {
        const mod = this.modules.find(m => m.moduleId === om.moduleId);
        if (mod) result.push(mod);
      });

      return result;
    },
    availableModules() {
      return this.optionalModules.filter(m => !this.selectedOptionalModuleIds.includes(m.moduleId));
    },
    showOptionalItems() {
      return true;
    },
  },
  watch: {
    props: {
      handler(val) {
        if (val && val.measureTemplates && val.measureTemplates.length && !this.modules.length) {
          this.initModules();
        }
      },
      immediate: true,
      deep: true,
    },
    value: {
      handler() {
        if (this.modules.length) {
          this.fillFromValue();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getUnitName(module) {
      if (module.units && module.units.length > 0 && module.units[0]) {
        return module.units[0].unitName || '';
      }
      return '';
    },
    initModules() {
      this.modules = cloneDeep(this.valueModules).map(vm => {
        return Object.assign({}, vm, { _value: '', _nrvPercent: '-' });
      });
    },
    fillFromValue() {
      // 确保 modules 已初始化且有 value 数据
      if (!this.modules.length || !this.value || !this.value.length) return;

      const templateData = this.value.find(v => v.measure_info && v.measure_info.template_id === this.templateId);
      if (!templateData || !templateData.measure_info) return;

      const extra = templateData.measure_info.extra || {};
      this.measureUnit = extra.measure_unit || '每100g';
      const values = templateData.measure_info.values || [];

      // 更新已选项
      const dataOptionalIds = values
        .map(v => v.module_id)
        .filter(mid => {
          const mod = this.valueModules.find(m => m.moduleId === mid);
          return mod && !mod.valueRequired;
        });
      this.selectedOptionalModuleIds = [...new Set([...this.selectedOptionalModuleIds, ...dataOptionalIds])];

      // 回填值
      this.modules.forEach(mod => {
        const matched = values.find(v => v.module_id === mod.moduleId);
        if (matched) {
          mod._value = matched.value || '';
          mod._nrvPercent = this.calcNrv(matched.value, mod.extra ? mod.extra.referenceValue : null);
        }
      });
    },
    // initFromValue(val) {
    //   if (!this.valueModules.length) return;

    //   const templateData = val.find(v => v.measure_info && v.measure_info.template_id === this.templateId);

    //   if (templateData && templateData.measure_info) {
    //     const extra = templateData.measure_info.extra || {};
    //     this.measureUnit = extra.measure_unit || '每100g';
    //     const values = templateData.measure_info.values || [];

    //     // 合并：保留已有的已选项 + 从数据中提取的已选项
    //     const dataOptionalIds = values
    //       .map(v => v.module_id)
    //       .filter(mid => {
    //         const mod = this.valueModules.find(m => m.moduleId === mid);
    //         return mod && !mod.valueRequired;
    //       });
    //     this.selectedOptionalModuleIds = [...new Set([...this.selectedOptionalModuleIds, ...dataOptionalIds])];

    //     this.modules = cloneDeep(this.valueModules).map(vm => {
    //       const matched = values.find(v => v.module_id === vm.moduleId);
    //       return Object.assign({}, vm, {
    //         _value: matched ? matched.value : '',
    //         _nrvPercent: this.calcNrv(matched ? matched.value : null, vm.extra ? vm.extra.referenceValue : null),
    //       });
    //     });
    //   } else {
    //     this.initModules();
    //   }
    // },
    calcNrv(value, referenceValue) {
      if (!value && value !== 0) return '-';
      if (!referenceValue && referenceValue !== 0) return '-';
      const numVal = Number(value);
      const numRef = Number(referenceValue);
      if (isNaN(numVal) || isNaN(numRef) || numRef === 0) return '-';
      return Math.round((numVal / numRef) * 100);
    },
    getNrvDisplay(nrvPercent) {
      if (nrvPercent === undefined || nrvPercent === null || nrvPercent === '') return '-';
      if (nrvPercent === '-') return '-';
      return nrvPercent + '%';
    },
    onValueChange(module) {
      module._nrvPercent = this.calcNrv(module._value, module.extra ? module.extra.referenceValue : null);
      this.emitValue();
    },
    onMeasureUnitChange() {
      this.emitValue();
    },
    emitValue() {
      const values = [];
      this.displayModules.forEach(mod => {
        if (mod._value !== '' && mod._value !== null && mod._value !== undefined) {
          const unit = mod.units && mod.units[0] ? mod.units[0] : {};
          values.push({
            module_id: mod.moduleId,
            value: String(mod._value),
            unit_id: unit.unitId || 0,
            unit_name: unit.unitName || '',
            prefix: mod.prefix,
            suffix: mod.suffix || '',
            value_required: mod.valueRequired,
            prefix_id: 0,
            suffix_id: 0,
            value_id: 0,
            extra: {
              is_sub_module: mod.extra && mod.extra.isSubModule ? mod.extra.isSubModule : 'false',
              nrv_percent: mod._nrvPercent === '-' ? '' : String(mod._nrvPercent),
            },
          });
        }
      });

      let name = '';
      values.forEach(v => {
        name += v.prefix + ' ' + v.value + v.unit_name + '、';
      });
      if (name) name = name.slice(0, -1);

      this.$emit('change', [
        {
          Value: 0,
          Name: name,
          PropertyId: this.props.propertyId,
          PropertyName: this.props.propertyName,
          diy_type: this.props.diyType,
          measure_info: {
            template_id: this.templateId,
            values,
            extra: {
              measure_unit: this.measureUnit,
            },
          },
        },
      ]);
    },
    deleteItem(moduleId) {
      this.selectedOptionalModuleIds = this.selectedOptionalModuleIds.filter(id => id !== moduleId);
      const mod = this.modules.find(m => m.moduleId === moduleId);
      if (mod) {
        mod._value = '';
        mod._nrvPercent = '-';
      }
      this.emitValue();
    },
    confirmAdd() {
      this.selectedModules.forEach(moduleId => {
        if (!this.selectedOptionalModuleIds.includes(moduleId)) {
          this.selectedOptionalModuleIds.push(moduleId);
        }
      });
      this.selectedModules = [];
      this.showAddDialog = false;
      this.$nextTick(() => {
        this.emitValue();
      });
    },
    validate() {
      return new Promise(resolve => {
        const hasError = this.displayModules.some(m => m.valueRequired && !m._value);
        resolve(!hasError);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.nutrition-table {
  width: 100%;
  display: inline-block;
  .nutrition-header {
    margin-bottom: 10px;
    .label {
      margin-right: 10px;
      font-size: 12px;
      color: #606266;
    }
  }
  .table {
    width: 600px;
    border-collapse: collapse;
    margin-bottom: 10px;
    th,
    td {
      border: 1px solid #ebeef5;
      padding: 8px 12px;
      text-align: left;
      font-size: 12px;
    }
    th {
      background-color: #fafafa;
      color: #606266;
      font-weight: 500;
    }
    .col-project {
      width: 30%;
    }
    .col-value {
      width: 30%;
    }
    .col-nrv {
      width: 20%;
      text-align: center;
    }
    .delete-icon {
      cursor: pointer;
      color: #909399;
      font-size: 16px;
      &:hover {
        color: #f56c6c;
      }
    }
    .required-mark {
      color: #f56c6c;
    }
    .error-text {
      position: relative;
    }
  }
  ::v-deep .el-button--text {
    padding: 0;
    font-size: 12px;
  }
}
</style>

<style lang="scss">
/* 弹窗样式不受 scoped 限制 */
.add-dialog-content {
  max-height: 400px;
  overflow-y: auto;
  .checkbox-item {
    display: inline-block;
    width: 200px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
  }
}
</style>
